import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { transfersAPI, productsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Package, Truck, Send, Clock, CheckCircle, Eye, RefreshCw,
  Building2, Store, PlayCircle, PackageCheck, FileText, MapPin,
  User, Phone, Calendar, Printer, AlertTriangle
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';

const statusConfig = {
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-amber-100 text-amber-700', icon: PlayCircle },
  packed: { label: 'Packed', color: 'bg-blue-100 text-blue-700', icon: PackageCheck },
  dispatched: { label: 'Dispatched', color: 'bg-purple-100 text-purple-700', icon: Truck },
  in_transit: { label: 'In Transit', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-teal-100 text-teal-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  discrepancy: { label: 'Discrepancy', color: 'bg-orange-100 text-orange-700', icon: AlertTriangle }
};

const WarehouseTransferOrders = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;
  
  // Main state
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialogs
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showDispatchDialog, setShowDispatchDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Dispatch form
  const [dispatchItems, setDispatchItems] = useState([]);
  const [dispatchInfo, setDispatchInfo] = useState({
    vehicle_number: '',
    vehicle_type: 'truck',
    driver_name: '',
    driver_mobile: '',
    transporter_name: '',
    expected_delivery: '',
    dispatch_notes: ''
  });

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      const params = { 
        page, 
        page_size: 20,
        source_id: warehouseId
      };
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      
      const response = await transfersAPI.listOrders(params);
      setOrders(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast({ title: 'Error', description: 'Failed to fetch transfer orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, page, statusFilter, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Get status badge
  const getStatusBadge = (status) => {
    const config = statusConfig[status];
    if (!config) return <Badge variant="outline">{status}</Badge>;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" /> {config.label}
      </Badge>
    );
  };

  // View order details
  const viewOrderDetails = async (order) => {
    try {
      const response = await transfersAPI.getOrder(order.id);
      setSelectedOrder(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch order details', variant: 'destructive' });
    }
  };

  // Start processing
  const handleStartProcessing = async (order) => {
    try {
      await transfersAPI.processOrder(order.id);
      toast({ title: 'Success', description: 'Order is now being processed' });
      fetchOrders();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to start processing', 
        variant: 'destructive' 
      });
    }
  };

  // Open dispatch dialog
  const openDispatchDialog = async (order) => {
    try {
      const response = await transfersAPI.getOrder(order.id);
      const orderData = response.data;
      
      setSelectedOrder(orderData);
      setDispatchItems(orderData.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        sku: item.sku,
        approved_qty: item.approved_qty,
        dispatched_qty: item.dispatched_qty || item.approved_qty,
        source_notes: item.source_notes || ''
      })));
      setDispatchInfo({
        vehicle_number: '',
        vehicle_type: 'truck',
        driver_name: '',
        driver_mobile: '',
        transporter_name: '',
        expected_delivery: '',
        dispatch_notes: ''
      });
      setShowDispatchDialog(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load order details', variant: 'destructive' });
    }
  };

  // Update dispatch quantity
  const updateDispatchQty = (productId, qty) => {
    setDispatchItems(items => items.map(item => 
      item.product_id === productId 
        ? { ...item, dispatched_qty: Math.max(0, parseFloat(qty) || 0) }
        : item
    ));
  };

  // Update source notes
  const updateSourceNotes = (productId, notes) => {
    setDispatchItems(items => items.map(item => 
      item.product_id === productId 
        ? { ...item, source_notes: notes }
        : item
    ));
  };

  // Handle dispatch
  const handleDispatch = async () => {
    if (!selectedOrder) return;
    
    // Validate
    if (!dispatchInfo.vehicle_number) {
      toast({ title: 'Error', description: 'Please enter vehicle number', variant: 'destructive' });
      return;
    }
    if (!dispatchInfo.driver_name) {
      toast({ title: 'Error', description: 'Please enter driver name', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      await transfersAPI.dispatchOrder(selectedOrder.id, {
        items: dispatchItems.map(item => ({
          product_id: item.product_id,
          dispatched_qty: item.dispatched_qty,
          source_notes: item.source_notes
        })),
        dispatch_info: dispatchInfo,
        generate_eway_bill: false
      });
      
      toast({ title: 'Success', description: 'Transfer order dispatched successfully' });
      setShowDispatchDialog(false);
      fetchOrders();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to dispatch order', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Print delivery order
  const printDeliveryOrder = (order) => {
    // Calculate totals
    let totalValue = 0;
    const itemsWithValue = order.items.map(item => {
      const qty = item.dispatched_qty || item.approved_qty || 0;
      const mrp = item.mrp || item.unit_price || 0;
      const lineTotal = qty * mrp;
      totalValue += lineTotal;
      return { ...item, qty, mrp, lineTotal };
    });
    
    // Open print view in new window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Delivery Order - ${order.transfer_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
            .info-box { border: 1px solid #ddd; padding: 10px; border-radius: 4px; }
            .info-box h4 { margin: 0 0 10px 0; font-size: 12px; color: #666; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .totals { margin-top: 20px; text-align: right; }
            .totals-table { width: 250px; margin-left: auto; }
            .totals-table td { border: none; padding: 4px 8px; }
            .totals-table .grand-total { font-size: 14px; font-weight: bold; border-top: 2px solid #333; }
            .signature { margin-top: 40px; display: flex; justify-content: space-between; }
            .signature div { text-align: center; }
            .signature-line { border-top: 1px solid #333; width: 150px; margin-top: 40px; padding-top: 5px; }
            .text-right { text-align: right; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>DELIVERY ORDER</h2>
            <h3>${order.transfer_number}</h3>
          </div>
          
          <div class="info-grid">
            <div class="info-box">
              <h4>FROM (Source)</h4>
              <strong>${order.source_name}</strong><br/>
              ${order.source_code || ''}<br/>
              GSTIN: ${order.source_gstin || 'N/A'}
            </div>
            <div class="info-box">
              <h4>TO (Destination)</h4>
              <strong>${order.destination_name}</strong><br/>
              ${order.destination_code || ''}<br/>
              GSTIN: ${order.destination_gstin || 'N/A'}
            </div>
          </div>
          
          ${order.dispatch_info?.vehicle_number ? `
          <div class="info-box" style="margin-bottom: 20px;">
            <h4>TRANSPORT DETAILS</h4>
            Vehicle: ${order.dispatch_info.vehicle_number} (${order.dispatch_info.vehicle_type || 'N/A'})<br/>
            Driver: ${order.dispatch_info.driver_name || 'N/A'} | Mobile: ${order.dispatch_info.driver_mobile || 'N/A'}
          </div>
          ` : ''}
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>SKU</th>
                <th>HSN</th>
                <th class="text-right">Qty</th>
                <th>UOM</th>
                <th class="text-right">MRP</th>
                <th class="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${itemsWithValue.map((item, idx) => `
                <tr>
                  <td>${idx + 1}</td>
                  <td>${item.product_name}</td>
                  <td>${item.sku}</td>
                  <td>${item.hsn_code || '-'}</td>
                  <td class="text-right">${item.qty}</td>
                  <td>${item.uom || 'PCS'}</td>
                  <td class="text-right">₹${(item.mrp || 0).toFixed(2)}</td>
                  <td class="text-right">₹${item.lineTotal.toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="totals">
            <table class="totals-table">
              <tr>
                <td>Total Items:</td>
                <td class="text-right">${order.items.length}</td>
              </tr>
              <tr>
                <td>Total Quantity:</td>
                <td class="text-right">${itemsWithValue.reduce((sum, i) => sum + i.qty, 0)}</td>
              </tr>
              <tr class="grand-total">
                <td>Goods Value:</td>
                <td class="text-right">₹${totalValue.toFixed(2)}</td>
              </tr>
            </table>
          </div>
          
          <div class="signature">
            <div>
              <div class="signature-line">Prepared By</div>
            </div>
            <div>
              <div class="signature-line">Checked By</div>
            </div>
            <div>
              <div class="signature-line">Received By</div>
            </div>
          </div>
          
          <script>window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Stats
  const pendingCount = orders.filter(o => o.status === 'approved').length;
  const processingCount = orders.filter(o => ['processing', 'packed'].includes(o.status)).length;
  const dispatchedCount = orders.filter(o => ['dispatched', 'in_transit'].includes(o.status)).length;

  return (
    <div className="p-6 space-y-6" data-testid="warehouse-transfer-orders-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Truck className="w-6 h-6 text-teal-600" />
            Transfer Orders
          </h1>
          <p className="text-gray-500">Process and dispatch stock transfers to stores</p>
        </div>
        <Button onClick={fetchOrders} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600">Pending Fulfillment</p>
                <p className="text-2xl font-bold text-emerald-800">{pendingCount}</p>
              </div>
              <Clock className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Processing</p>
                <p className="text-2xl font-bold text-amber-800">{processingCount}</p>
              </div>
              <PlayCircle className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Dispatched</p>
                <p className="text-2xl font-bold text-purple-800">{dispatchedCount}</p>
              </div>
              <Truck className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Orders</p>
                <p className="text-2xl font-bold text-blue-800">{orders.length}</p>
              </div>
              <Package className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-gray-600">Status:</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved (Pending)</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="packed">Packed</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Transfer #</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    Loading orders...
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                    <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No transfer orders found</p>
                    <p className="text-sm">Orders will appear here when HQ approves stock requests</p>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.id} className="hover:bg-gray-50" data-testid={`transfer-order-row-${order.id}`}>
                    <TableCell>
                      <span className="font-mono text-sm text-blue-600 font-medium">
                        {order.transfer_number}
                      </span>
                      {order.stock_request_number && (
                        <p className="text-xs text-gray-500">
                          Req: {order.stock_request_number}
                        </p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {order.destination_type === 'store' ? (
                          <Store className="w-4 h-4 text-teal-500" />
                        ) : (
                          <Building2 className="w-4 h-4 text-purple-500" />
                        )}
                        <div>
                          <p className="font-medium text-gray-800">{order.destination_name}</p>
                          <p className="text-xs text-gray-500">{order.destination_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {order.items?.length || 0}
                    </TableCell>
                    <TableCell className="text-right font-medium text-gray-800">
                      ₹{(order.grand_total || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell>{getStatusBadge(order.status)}</TableCell>
                    <TableCell className="text-gray-500 text-sm">
                      {formatDate(order.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewOrderDetails(order)}
                          className="text-gray-600 hover:text-blue-600"
                          data-testid={`transfer-view-btn-${order.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {order.status === 'approved' && (
                          <Button 
                            size="sm"
                            onClick={() => handleStartProcessing(order)}
                            className="bg-amber-500 hover:bg-amber-600 text-white gap-1"
                            data-testid={`transfer-process-btn-${order.id}`}
                          >
                            <PlayCircle className="w-4 h-4" /> Process
                          </Button>
                        )}
                        
                        {['processing', 'packed'].includes(order.status) && (
                          <Button 
                            size="sm"
                            onClick={() => openDispatchDialog(order)}
                            className="bg-purple-600 hover:bg-purple-700 text-white gap-1"
                            data-testid={`transfer-dispatch-btn-${order.id}`}
                          >
                            <Send className="w-4 h-4" /> Dispatch
                          </Button>
                        )}
                        
                        {['dispatched', 'in_transit', 'completed'].includes(order.status) && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => printDeliveryOrder(order)}
                            className="gap-1"
                          >
                            <Printer className="w-4 h-4" /> Print DO
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-gray-600">
            Page {page} of {totalPages}
          </span>
          <Button 
            variant="outline" 
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Order Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Package className="w-5 h-5 text-blue-600" />
              Transfer Order - {selectedOrder?.transfer_number}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Status & Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedOrder.status)}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Transfer Type</Label>
                  <p className="font-medium text-gray-800 capitalize mt-1">
                    {selectedOrder.transfer_type?.replace(/_/g, ' ')}
                  </p>
                </div>
              </div>

              {/* Source & Destination */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
                  <Label className="text-xs text-teal-600">Source (From)</Label>
                  <p className="font-medium text-gray-800 mt-1">{selectedOrder.source_name}</p>
                  <p className="text-sm text-gray-500">{selectedOrder.source_code}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <Label className="text-xs text-purple-600">Destination (To)</Label>
                  <p className="font-medium text-gray-800 mt-1">{selectedOrder.destination_name}</p>
                  <p className="text-sm text-gray-500">{selectedOrder.destination_code}</p>
                  {selectedOrder.destination_store_type === 'franchise' && (
                    <Badge className="mt-1 bg-orange-100 text-orange-700">Franchise</Badge>
                  )}
                </div>
              </div>

              {/* Dispatch Info */}
              {selectedOrder.dispatch_info?.vehicle_number && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-xs text-blue-600">Dispatch Details</Label>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>
                      <p className="text-gray-500">Vehicle</p>
                      <p className="font-medium">{selectedOrder.dispatch_info.vehicle_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Driver</p>
                      <p className="font-medium">{selectedOrder.dispatch_info.driver_name}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <Label className="text-gray-700 font-medium">Items</Label>
                <div className="border rounded-lg overflow-hidden mt-2">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Approved</TableHead>
                        <TableHead className="text-right">Dispatched</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedOrder.items?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <p className="font-medium text-gray-800">{item.product_name}</p>
                            <p className="text-xs text-gray-500">{item.sku}</p>
                          </TableCell>
                          <TableCell className="text-right">{item.approved_qty}</TableCell>
                          <TableCell className="text-right">
                            {item.dispatched_qty || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            ₹{(item.transfer_price || 0).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{(item.line_total || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Totals */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>₹{(selectedOrder.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Tax:</span>
                    <span>₹{(selectedOrder.total_tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2 mt-2">
                    <span>Grand Total:</span>
                    <span>₹{(selectedOrder.grand_total || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {selectedOrder && ['dispatched', 'in_transit', 'completed'].includes(selectedOrder.status) && (
              <Button variant="outline" onClick={() => printDeliveryOrder(selectedOrder)}>
                <Printer className="w-4 h-4 mr-2" /> Print Delivery Order
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispatch Dialog */}
      <Dialog open={showDispatchDialog} onOpenChange={setShowDispatchDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Send className="w-5 h-5 text-purple-600" />
              Dispatch Transfer - {selectedOrder?.transfer_number}
            </DialogTitle>
            <DialogDescription>
              To: {selectedOrder?.destination_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Transport Details */}
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Label className="text-gray-700 font-medium mb-3 block">Transport Details</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Vehicle Number *</Label>
                  <Input 
                    placeholder="e.g., MH12AB1234"
                    data-testid="dispatch-vehicle-number"
                    value={dispatchInfo.vehicle_number}
                    onChange={(e) => setDispatchInfo({...dispatchInfo, vehicle_number: e.target.value.toUpperCase()})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Vehicle Type</Label>
                  <Select 
                    value={dispatchInfo.vehicle_type} 
                    onValueChange={(v) => setDispatchInfo({...dispatchInfo, vehicle_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="truck">Truck</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="tempo">Tempo</SelectItem>
                      <SelectItem value="bike">Bike</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Driver Name *</Label>
                  <Input 
                    placeholder="Driver full name"
                    data-testid="dispatch-driver-name"
                    value={dispatchInfo.driver_name}
                    onChange={(e) => setDispatchInfo({...dispatchInfo, driver_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Driver Mobile</Label>
                  <Input 
                    placeholder="10-digit mobile number"
                    value={dispatchInfo.driver_mobile}
                    onChange={(e) => setDispatchInfo({...dispatchInfo, driver_mobile: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Transporter Name</Label>
                  <Input 
                    placeholder="Transport company (optional)"
                    value={dispatchInfo.transporter_name}
                    onChange={(e) => setDispatchInfo({...dispatchInfo, transporter_name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-gray-600">Expected Delivery</Label>
                  <Input 
                    type="date"
                    value={dispatchInfo.expected_delivery}
                    onChange={(e) => setDispatchInfo({...dispatchInfo, expected_delivery: e.target.value})}
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div>
              <Label className="text-gray-700 font-medium mb-2 block">Items to Dispatch</Label>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right w-24">Approved</TableHead>
                      <TableHead className="text-right w-32">Dispatch Qty</TableHead>
                      <TableHead className="w-40">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dispatchItems.map((item) => (
                      <TableRow key={item.product_id}>
                        <TableCell>
                          <p className="font-medium text-gray-800">{item.product_name}</p>
                          <p className="text-xs text-gray-500">{item.sku}</p>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {item.approved_qty}
                        </TableCell>
                        <TableCell className="text-right">
                          <Input 
                            type="number"
                            min="0"
                            max={item.approved_qty}
                            value={item.dispatched_qty}
                            onChange={(e) => updateDispatchQty(item.product_id, e.target.value)}
                            className="w-24 text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input 
                            placeholder="Notes..."
                            value={item.source_notes}
                            onChange={(e) => updateSourceNotes(item.product_id, e.target.value)}
                            className="text-sm"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Dispatch Notes */}
            <div className="space-y-2">
              <Label className="text-gray-600">Dispatch Notes (Optional)</Label>
              <Textarea 
                placeholder="Add any notes about this dispatch..."
                value={dispatchInfo.dispatch_notes}
                onChange={(e) => setDispatchInfo({...dispatchInfo, dispatch_notes: e.target.value})}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDispatchDialog(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleDispatch}
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-dispatch-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? 'Dispatching...' : 'Confirm Dispatch'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseTransferOrders;
